<template>
  <section
    id="message-register"
    class="boss-create-new-scout-container"
  >
    <h2
      class="boss-new-scout-title"
      data-test="scout-title"
    >
      {{ title }}
    </h2>
    <!-- ▼会員情報 -->
    <scout-create-target-members
      :is-edit="isEdit"
      :members="members"
      @setUserChecked="setUserChecked"
    />
    <!-- ▲会員情報 -->
    <template v-if="isEdit">
      <!-- ▼入力フォーム -->
      <scout-create-message
        v-bind.sync="message"
        :special-favor-ids.sync="specialFavorIds"
        :send-date.sync="reservation.sendDate"
        :send-time.sync="reservation.sendTime"
        :reservation-name.sync="reservation.name"
        :is-immediately.sync="isImmediately"
        :checked-user-ids="checkedUserIds"
        :scout-end-date="scoutEndDate"
        :reservation-id="Number($route.params.reservationId)"
        :is-scouted-for-transition="isScoutedForTransition"
        @showDetail="showDetail"
      />
      <!-- ▲入力フォーム -->
    </template>
    <template v-else>
      <!-- ▼詳細 -->
      <scout-create-message-detail
        :subject="message.subject"
        :body="message.body"
        :is-scout-send-disabled="isScoutSendDisabled"
        :special-favor-ids="specialFavorIds"
        :reservation-date="reservationDate"
        :reservation-name="reservation.name"
        @switchingState="switchingState"
        @registerScoutMessages="registerScoutMessages"
      />
      <!-- ▲詳細 -->
    </template>
    <!-- ▼スカウト作成 Container -->
  </section>
</template>

<script>
import ScoutCreateTargetMembers from '@/components/organisms/ScoutCreateTargetMembers'
import ScoutCreateMessage from '@/components/organisms/ScoutCreateMessage'
import ScoutCreateMessageDetail from '@/components/organisms/ScoutCreateMessageDetail'
import { mapGetters, mapMutations } from 'vuex'
import Indicator from '@/lib/indicator'
import _ from 'lodash'
import { createDate } from '@/lib/dateUtils.js'

export default {
  components: {
    ScoutCreateTargetMembers,
    ScoutCreateMessage,
    ScoutCreateMessageDetail
  },
  beforeRouteEnter (_, from, next) {
    next(vm => {
      vm.isScoutedForTransition = from.name === 'scoutScouted'
    })
  },
  async beforeRouteLeave (to, from, next) {
    let leave = true
    if ((this.message.subject || this.message.body || this.specialFavorIds.length) && !this.canTransition) {
      await this.$dialog.confirm('編集中のデータがあります。他のページに移動してよろしいですか？').catch(() => {
        leave = false
      })
    }
    leave ? next() : next(false)
  },
  props: {
    // userIdとSearchConditionName
    idAndNameObjects: {
      type: Array,
      default: () => ([])
    }
  },
  data () {
    return {
      members: [],
      message: {
        subject: '',
        body: ''
      },
      specialFavorIds: [],
      isEdit: true,
      canTransition: false,
      isScoutSendDisabled: false,
      reservation: {
        name: '',
        sendDate: null,
        sendTime: null
      },
      isImmediately: true,
      targetUsers: this.idAndNameObjects,
      includeFirstSend: true,
      isScoutedForTransition: false
    }
  },
  computed: {
    title () {
      return this.isEdit ? 'スカウト作成' : 'スカウト送信内容の確認'
    },
    reservationDate () {
      const date = _.get(this.reservation, 'sendDate')
      const time = _.get(this.reservation, 'sendTime')
      return !this.isImmediately && date && time ? `${date} ${time}` : ''
    },
    reservationRequestParam () {
      return {
        message: this.message,
        users: this.registerTargetUsers,
        specialFavorIds: this.specialFavorIds,
        scoutReservationName: this.reservation.name,
        reservationDate: createDate(this.reservationDate)?.format('YYYY-MM-DDTHH:mm')
      }
    },
    checkedUserIds () {
      return _(this.members).filter('checked').map('id').value()
    },
    registerTargetUsers () {
      return this.targetUsers.filter(targetUser => this.checkedUserIds.includes(targetUser.userId))
    },
    ...mapGetters('contracts', {
      jobBy: 'jobBy'
    }),
    scoutEndDate () {
      const job = this.jobBy(this.$route.params.contractId, this.$route.params.jobId)
      return this.includeFirstSend ? job.endDate : job.scoutEndDate
    }
  },
  watch: {
    targetUsers: {
      handler (val, oldVal) {
        if (val.length && !_.isEqual(val, oldVal)) {
          this.fetchMembers()
        }
      }
    }
  },
  created () {
    this.$route.params.reservationId && this.fetchReservation()
    this.targetUsers.length && this.fetchMembers()
  },
  methods: {
    showDetail () {
      this.switchingState(false)
      this.$scrollTo('#message-register', 0, { offset: -35 })
    },
    switchingState (isEdit) {
      this.$set(this, 'isEdit', isEdit)
    },
    registerScoutMessages () {
      this.$set(this, 'isScoutSendDisabled', true)
      if (this.isImmediately) {
        this.send(this.registerTargetUsers)
      } else {
        // スカウト予約
        this.$route.params.reservationId ? this.updateReservation() : this.registerReservation()
      }
    },
    ...mapMutations('contracts', [
      'fetchScoutAmount'
    ]),
    send (registerTargetUsers) {
      const requestParam = {
        message: this.message,
        users: registerTargetUsers || [],
        specialFavorIds: this.specialFavorIds
      }
      Indicator.emitShow()
      this.$http.secured.post(`scout/message/send/scouts/contracts/${this.$route.params.contractId}/jobs/${this.$route.params.jobId}/scouts`, requestParam).then((response) => {
        this.$toasted.global.success({ message: `<div><div>スカウトを送信しました</div><div style="margin-top: 4px">（送信完了${response.data.successCount}名／送信対象${response.data.totalCount}名）</div></div>` })
        this.applyMessage()
        Indicator.emitHide()
        this.$router.back()
      }).catch(() => {
        Indicator.emitHide()
        this.$set(this, 'isScoutSendDisabled', false)
      })
    },
    registerReservation () {
      const requestParam = this.reservationRequestParam
      Indicator.emitShow()
      this.$http.secured.post(`scout/message/send/scouts/contracts/${this.$route.params.contractId}/jobs/${this.$route.params.jobId}/scouts/reservations`, requestParam).then(() => {
        this.$toasted.global.success({ message: 'スカウト予約しました' })
        this.applyMessage()
        Indicator.emitHide()
        this.$router.push({ name: 'scoutReservation' })
      }).catch(() => {
        Indicator.emitHide()
        this.$set(this, 'isScoutSendDisabled', false)
      })
    },
    updateReservation () {
      const requestParam = this.reservationRequestParam
      Indicator.emitShow()
      this.$http.secured.put(`scout/message/send/scouts/contracts/${this.$route.params.contractId}/jobs/${this.$route.params.jobId}/scouts/reservations/${this.$route.params.reservationId}`, requestParam).then(() => {
        this.$toasted.global.success({ message: 'スカウト予約を更新しました' })
        this.applyMessage()
        Indicator.emitHide()
        this.$router.push({ name: 'scoutReservation' })
      }).catch(() => {
        Indicator.emitHide()
        this.$set(this, 'isScoutSendDisabled', false)
      })
    },
    fetchReservation () {
      return this.$http.secured.get(`/scout/message/send/scouts/contracts/${this.$route.params.contractId}/jobs/${this.$route.params.jobId}/scouts/reservations/${this.$route.params.reservationId}`)
        .then(response => {
          this.$set(this.message, 'subject', response.data.subject)
          this.$set(this.message, 'body', response.data.body)
          this.$set(this, 'specialFavorIds', response.data.specialFavors.map(specialFavor => specialFavor.id))
          this.$set(this, 'targetUsers', response.data.targetUsers)
          this.$set(this, 'isImmediately', false)
          this.$set(this.reservation, 'name', response.data.name)
          this.$set(this.reservation, 'sendDate', createDate(response.data.sendDate)?.format('YYYY-MM-DD'))
          this.$set(this.reservation, 'sendTime', createDate(response.data.sendDate)?.format('HH:mm'))
        }).catch(() => {
          this.$router.push({ name: 'scoutReservation' })
        })
    },
    fetchMembers () {
      this.$http.secured.post(`/scout/view/contracts/${this.$route.params.contractId}/jobs/${this.$route.params.jobId}/users`, this.targetUsers.map(idAndNameObject => idAndNameObject.userId))
        .then(response => {
          this.$set(this, 'members', response.data.users.map(user => _.set(user, 'checked', true)))
          this.$set(this, 'includeFirstSend', response.data.includeFirstSend)
        })
    },
    applyMessage () {
      this.fetchScoutAmount({ contractId: this.$route.params.contractId })
      this.$set(this, 'canTransition', true)
      this.$set(this, 'isScoutSendDisabled', false)
    },
    setUserChecked (checked, index) {
      this.$set(this.members[index], 'checked', checked)
    }
  }
}
</script>
